import { FLOW_CONTROL_SOCKET_TYPE } from '@erp/workflows';

/**
 * Check if two socket types are compatible for a workflow edge (output → input).
 */
export function areSocketTypesCompatible(
  sourceType: string,
  targetType: string,
): boolean {
  // Flow control INPUT sockets accept ANY type (acts as a "wait for completion" signal)
  // This allows any output to trigger a flow control input
  if (targetType === FLOW_CONTROL_SOCKET_TYPE) {
    return true;
  }

  // Flow control OUTPUT sockets can ONLY connect to flow control inputs
  // (already handled above - if we get here, target is not flow control)
  if (sourceType === FLOW_CONTROL_SOCKET_TYPE) {
    return false;
  }

  // 'any' is compatible with everything
  if (sourceType === 'any' || targetType === 'any') {
    return true;
  }

  // Exact match
  if (sourceType === targetType) {
    return true;
  }

  // Arrays - check if element types match (must come before json/string checks)
  if (sourceType.startsWith('array<') && targetType.startsWith('array<')) {
    const sourceElementType = sourceType.slice(6, -1); // Extract 'string' from 'array<string>'
    const targetElementType = targetType.slice(6, -1);
    return areSocketTypesCompatible(sourceElementType, targetElementType);
  }

  // Array output to single input - allow connecting array<X> to X
  // This enables workflows where array items are processed individually or spread
  if (sourceType.startsWith('array<')) {
    const sourceElementType = sourceType.slice(6, -1); // Extract element type from 'array<X>'
    return areSocketTypesCompatible(sourceElementType, targetType);
  }

  // Single input to array output - allow connecting X to array<X>
  // This enables fan-in patterns where multiple sources combine into one array
  // The backend will concatenate the arrays
  if (targetType.startsWith('array<')) {
    const targetElementType = targetType.slice(6, -1); // Extract element type from 'array<X>'
    return areSocketTypesCompatible(sourceType, targetElementType);
  }

  // JSON can accept most types (it's a flexible container)
  if (targetType === 'json') {
    // Accept primitives and any entity_reference (typed or untyped)
    if (sourceType.startsWith('entity_reference')) {
      return true;
    }
    return ['string', 'number', 'boolean', 'json', 'uuid', 'date'].includes(
      sourceType,
    );
  }

  // String can accept many types (implicit conversion)
  if (targetType === 'string') {
    return ['string', 'number', 'boolean', 'uuid', 'date'].includes(sourceType);
  }

  // Entity reference compatibility - must match specific entity type
  // e.g., "entity_reference:ApInvoice" is only compatible with "entity_reference:ApInvoice"
  if (
    sourceType.startsWith('entity_reference') &&
    targetType.startsWith('entity_reference')
  ) {
    // If either is untyped (just "entity_reference"), allow connection
    if (
      sourceType === 'entity_reference' ||
      targetType === 'entity_reference'
    ) {
      return true;
    }
    // Both are typed - must match exactly
    return sourceType === targetType;
  }

  return false;
}

function humanizeSocketType(t: string): string {
  if (t === FLOW_CONTROL_SOCKET_TYPE) {
    return 'a flow-order connector';
  }
  if (t === 'any') {
    return 'any type';
  }
  if (t === 'uuid') {
    return 'a UUID';
  }
  if (t === 'string') {
    return 'text';
  }
  if (t === 'number') {
    return 'a number';
  }
  if (t === 'boolean') {
    return 'true/false';
  }
  if (t === 'json') {
    return 'structured JSON';
  }
  if (t === 'date') {
    return 'a date';
  }
  if (t === 'signal') {
    return 'a signal';
  }
  if (t.startsWith('array<') && t.endsWith('>')) {
    const inner = t.slice(6, -1);
    return `a list of ${humanizeSocketType(inner)}`;
  }
  if (t.startsWith('entity_reference:')) {
    const label = t
      .slice('entity_reference:'.length)
      .replace(/_/g, ' ')
      .toLowerCase();
    return `a ${label} reference`;
  }
  if (t.startsWith('entity_reference')) {
    return 'an entity reference';
  }
  return t;
}

/**
 * When an output type cannot wire into an input type, returns a short sentence for UI (toast/tooltip).
 */
export function explainSocketTypeIncompatibility(
  outputType: string,
  inputType: string,
): string | null {
  if (areSocketTypesCompatible(outputType, inputType)) {
    return null;
  }

  if (outputType === FLOW_CONTROL_SOCKET_TYPE) {
    return '“After complete” only connects to “Wait for” on another step—not to a data input.';
  }

  const out = humanizeSocketType(outputType);
  const inp = humanizeSocketType(inputType);
  return `That output is ${out}, but this input expects ${inp}.`;
}
